import { Component, OnInit, HostBinding, OnDestroy, Inject, HostListener } from '@angular/core';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';
import { DOCUMENT, Location } from '@angular/common';

import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

import { LanguageService } from '@fcom/ui-translate';
import { FinnairOrder } from '@fcom/dapi/api/models';
import { snapshot } from '@fcom/rx';
import { IdAndHash } from '@fcom/dapi';
import { OrderStatus, orderData, orderStatus, orderFetchInformation, OrderActions } from '@fcom/common-booking/store';
import { BookingOrderService } from '@fcom/common-booking';
import { PurchaseSuccessService } from '@fcom/common-booking/modules/purchase-success';
import { pathSegmentsToPathString } from '@fcom/core/utils/app-url-utils';
import { ElementActions, ElementTypes, GaContext, UserAgentService } from '@fcom/common/index';
import { GtmService } from '@fcom/common/gtm';
import { ConfigService } from '@fcom/core/index';
import { BookingAndAppState } from '@fcom/common/interfaces/booking';

@Component({
  selector: 'fin-booking-purchase-success-route',
  templateUrl: './booking-purchase-success-route.component.html',
})
export class BookingPurchaseSuccessRouteComponent implements OnInit, OnDestroy {
  @HostBinding('class') classAttribute = 'grid-col-12 pr-clear';
  @HostListener('window:beforeinstallprompt') onBeforeInstallPrompt(): void {
    if (!this.userAgentService.isAndroid()) {
      return;
    }

    this.gtmService.trackElement(
      'confirmation-page_android_prompt_show',
      GaContext.BOOKING_FLOW,
      ElementTypes.BANNER,
      undefined,
      ElementActions.VIEW
    );
  }

  @HostListener('window:appinstalled') onAppInstalled(): void {
    if (!this.userAgentService.isAndroid()) {
      return;
    }

    this.gtmService.trackElement(
      'confirmation-page_android_prompt_install',
      GaContext.BOOKING_FLOW,
      ElementTypes.BANNER,
      undefined,
      ElementActions.CLICK
    );
  }

  readonly orderData$: Observable<FinnairOrder>;
  readonly orderStatus$: Observable<OrderStatus>;
  readonly orderFetchInformation$: Observable<IdAndHash>;

  constructor(
    private store$: Store<BookingAndAppState>,
    private bookingOrderService: BookingOrderService,
    private router: Router,
    private languageService: LanguageService,
    private activatedRoute: ActivatedRoute,
    private purchaseSuccessService: PurchaseSuccessService,
    private locationService: Location,
    private gtmService: GtmService,
    private configService: ConfigService,
    private userAgentService: UserAgentService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.orderData$ = this.store$.pipe(
      orderData(),
      filter((v) => !!v)
    );
    this.orderStatus$ = this.store$.pipe(orderStatus());
    this.orderFetchInformation$ = this.store$.pipe(
      orderFetchInformation(),
      filter((v) => !!v)
    );
  }

  ngOnInit(): void {
    const paramMap: ParamMap = this.activatedRoute.snapshot.queryParamMap;
    if (this.purchaseSuccessService.hasOrderQueryParams(paramMap)) {
      this.store$.dispatch(
        OrderActions.setOrderFetchInformation({
          orderInformation: {
            id: paramMap.get(this.purchaseSuccessService.PARAM_ORDER_ID),
            hash: paramMap.get(this.purchaseSuccessService.PARAM_HASH),
          },
        })
      );
    }

    if (this.userAgentService.isAndroid()) {
      this.addAppInstallPromptAndroid();
    } else if (this.userAgentService.isIos()) {
      this.addAppInstallPromptIos();
    }

    const order: FinnairOrder = snapshot(this.store$.pipe(orderData()));
    const status: OrderStatus = snapshot(this.store$.pipe(orderStatus()));
    const fetchInformation: IdAndHash = snapshot(this.store$.pipe(orderFetchInformation()));

    if (this.purchaseSuccessService.orderHasToBeRefetched(order, status, fetchInformation, paramMap)) {
      this.fetchOrder();
    }

    // Remove sensitive param from history and browser url
    this.locationService.replaceState(pathSegmentsToPathString(this.activatedRoute.snapshot.pathFromRoot));
  }

  ngOnDestroy(): void {
    this.removeAppInstallPrompts();
  }

  fetchOrder(): void {
    this.bookingOrderService.queueOrderLoad(false, true);
  }

  navigateToFrontPage(): void {
    this.router.navigateByUrl(`/${this.languageService.langValue}`);
  }

  addAppInstallPromptIos(): void {
    const appName = this.configService.cfg.appName;
    const appId = this.configService.cfg.appIdIos;
    const head = this.document.querySelector('head');
    const metaTag = this.document.createElement('meta');

    metaTag.setAttribute('data-test', 'install-prompt-ios');
    metaTag.setAttribute('name', appName);
    metaTag.setAttribute('content', `app-id=${appId}`);
    head.insertBefore(metaTag, head.firstChild);
  }

  addAppInstallPromptAndroid(): void {
    const manifestPath = this.configService.cfg.manifestPathAndroid;
    const head = this.document.querySelector('head');
    const linkTag = this.document.createElement('link');

    linkTag.setAttribute('data-test', 'install-prompt-android');
    linkTag.setAttribute('rel', 'manifest');
    linkTag.setAttribute('crossorigin', 'use-credentials');
    linkTag.setAttribute('href', manifestPath);
    head.insertBefore(linkTag, head.firstChild);
  }

  removeAppInstallPrompts(): void {
    this.document
      .querySelectorAll('[data-test="install-prompt-ios"], [data-test="install-prompt-android"]')
      .forEach((e) => e.remove());
  }
}
